<script lang="ts">
import { computed, defineComponent, inject, Ref, ref, toRaw, UnwrapRef } from 'vue';
import InputPiece from 'components/Basket/InputPiece.vue';
import { BasketStore } from 'stores/Basket/Basket.ts';
import { PageStore } from 'stores/Page/Page.ts';
import { storeToRefs } from 'pinia';
import { BasketProductItem } from 'src/core/types/Basket';
import { ResponseContext } from 'src/core/api/ProviderContext';
import { useMeta, useQuasar } from 'quasar';
import { useRouter } from 'vue-router';
import Breadcrumb from 'components/Template/Breadcrumb.vue';

export default defineComponent({
  name: 'Basket',

  components: { Breadcrumb, InputPiece },

  setup() {
    const isLogged = inject<Ref<boolean>>('isLogged');

    const pageStore = PageStore();
    const { content } = storeToRefs(pageStore);

    const basketStore = BasketStore();
    const { basketList, userBasketCount, noData } = storeToRefs(basketStore);

    const $q = useQuasar();
    const $router = useRouter();

    const basketListLoading = ref(false);
    const productQuantity: Ref<{ [variationCode: number]: Ref<number> }> = ref({});
    const basketQuantityLoading = ref(false);
    const checkInBasketLoading = ref(false);

    const shippingCost = content.value.shippingCost;

    const productsTotal: Ref<{ [variationCode: number]: Ref<number> }> = ref({});

    const stockOutHasExists = computed(() => basketStore.stockOutHasExists);
    const notSaleHasExists = computed(() => basketStore.notSaleHasExists);
    const outOfQuantity = computed(() => basketStore.outOfQuantity);
    const hasBasketIssues = computed(() => stockOutHasExists.value || notSaleHasExists.value || outOfQuantity.value);

    basketListLoading.value = true;
    basketStore.getBasketList().then(() => {
      basketList.value.forEach((product: BasketProductItem) => {
        const [variation] = product.variations;
        productQuantity.value[variation.code] = ref(product.quantity);
        productsTotal.value[variation.code] = ref(variation.priceNumber);
      });

      // stockOutHasExists.value = basketList.value.find((product: BasketProductItem) => !product.instock);
      basketListLoading.value = false;
    });

    useMeta({
      titleTemplate: (title) => `${title} | Sepetim`,
      meta: {
        description: { name: 'description', content: 'Sepetteki ürünlerim' },
      },
    });

    return {
      isLogged,
      $q,
      basketListLoading,
      basketQuantityLoading,
      basketList,
      userBasketCount,
      productQuantity,
      productsTotal,
      addToBasket: basketStore.addToBasket,
      removeFromBasket: basketStore.removeFromBasket,
      shippingCost,
      checkInBasketLoading,
      stockOutHasExists,
      notSaleHasExists,
      hasBasketIssues,
      outOfQuantity,
      checkInUserBasket() {
        checkInBasketLoading.value = true;
        basketStore
          .checkInBasket()
          .then((data: BasketProductItem[]) => {
            // stockOutHasExists.value = data.find((product: BasketProductItem) => !product.instock);

            if (!isLogged?.value) {
              window.location.replace('/login-or-register');
              return;
            }

            if (!stockOutHasExists.value && !hasBasketIssues.value) {
              $router.push('/complete-order');
            }
            checkInBasketLoading.value = false;
          })
          .catch(() => {
            checkInBasketLoading.value = false;
          });
      },
    };
  },

  methods: {
    getVariationDetail(variationAttrs: UnwrapRef<BasketProductItem['attributes']>) {
      const attrArr = variationAttrs.map((o) => o.name);

      return attrArr.join(', ');
    },

    getVariationImage(variations: BasketProductItem['variations']) {
      const [variation] = variations,
        [firstImage] = variation.images;
      return firstImage;
    },

    getProductsTotal() {
      const quantities = this.productQuantity;
      return Object.entries(this.productsTotal)
        .map((o) => Number(o[1]) * Number(quantities[Number(o[0])]))
        .reduce((partialSum, a) => partialSum + a, 0);
    },

    increaseQuantity(productCode: number, variationCode: number, quantity: number) {
      this.basketQuantityLoading = true;
      const product = this.basketList.find((o: BasketProductItem) => o.code === productCode && o.variations[0].code === variationCode);
      const addTo = quantity - product.quantity;
      this.addToBasket(variationCode, addTo)
        .then(({ status, message, data }: ResponseContext) => {
          if (status._200_) {
            this.$q.notify({
              color: 'green',
              icon: 'fa-regular fa-circle-check',
              message: 'Ürün miktarını artırdınız',
              badgeColor: 'white',
              badgeTextColor: 'green-10',
            });

            this.basketList = data;
            this.userBasketCount = data.length;
          } else {
            this.$q.notify({
              color: 'red',
              icon: 'fa-solid fa-circle-exclamation',
              message,
            });

            this.productQuantity[variationCode] = product.quantity;
          }
          this.basketQuantityLoading = false;
        })
        .catch(() => {
          this.basketQuantityLoading = false;
        });
    },

    decreaseQuantity(productCode: number, variationCode: number, quantity: number) {
      this.basketQuantityLoading = true;
      const product = this.basketList.find((o: BasketProductItem) => o.code === productCode && o.variations[0].code === variationCode);
      const removeTo = product.quantity - quantity;

      if (removeTo < 1) return;

      this.removeFromBasket(variationCode, removeTo)
        .then(({ status, message, data }: ResponseContext) => {
          if (status._200_) {
            this.$q.notify({
              color: 'green',
              icon: 'fa-regular fa-circle-check',
              badgeColor: 'white',
              badgeTextColor: 'green-10',
              message: quantity > 0 ? 'Ürün miktarını azalttınız' : 'Ürün sepetten kaldırılmıştır',
            });

            this.basketList = data;
            this.userBasketCount = data.length;
          } else {
            this.$q.notify({
              color: 'red',
              icon: 'fa-solid fa-circle-exclamation',
              message,
            });

            this.productQuantity[variationCode] = product.quantity;
          }
          this.basketQuantityLoading = false;
        })
        .catch(() => {
          this.basketQuantityLoading = false;
        });
    },

    removeProductFromBasket(productCode: number, variationCode: number, quantity: number) {
      return this.decreaseQuantity(productCode, variationCode, -(quantity * 2));
    },
  },
});
</script>

<template>
  <section id="userBasketList">
    <div class="container">
      <div class="row">
        <div class="col-xs-12 q-pa-md"></div>

        <!--        <div class="site-breadcrumb col-12 q-py-md q-mb-md">-->
        <!--          <q-breadcrumbs class="text-grey" active-color="primary">-->
        <!--            <template v-slot:separator>-->
        <!--              <q-icon size="1em" name="arrow_forward" color="primary" />-->
        <!--            </template>-->

        <!--            <q-breadcrumbs-el label="Anasayfa" icon="home" href="/" />-->
        <!--            <q-breadcrumbs-el label="Sepetim" />-->
        <!--          </q-breadcrumbs>-->
        <!--        </div>-->
        <div class="col-12">
          <breadcrumb :model-value="[{ name: 'Sepetim', link: '' }]" />
        </div>

        <div class="user-basket col-xs-12 col-md-8 q-pa-md">
          <q-list>
            <q-item class="q-px-none">
              <q-item-section>
                <q-item-label class="basket-list--head-title text-subtitle1 flex items-center">
                  <q-icon name="shopping_cart" size="1.3em" class="q-mr-sm" />
                  Sepet Listem
                </q-item-label>
                <q-item-label>
                  <q-separator />
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-item v-if="hasBasketIssues" class="basket-list--no-stock-list items-center bg-red-1 q-mb-sm">
              <q-item-section class="text-center border">
                <p class="basket-list--no-stock-warn flex justify-center items-center text-red-14">
                  <q-img src="/img/sad-cute.png" width="50px" class="q-mr-md" />
                  <strong class="q-mr-xs">Üzgünüz</strong> bazı ürünlerin stok durumu değişmiş. Lütfen stokta olmayan ürünleri kaldırın veya miktar azaltın.
                </p>
              </q-item-section>
            </q-item>
            <q-item
              :key="key"
              v-for="(product, key) in basketList"
              class="text-black q-py-md"
              :class="product.instock && product.onsale && product.inquantity ? 'bg-grey-3' : 'bg-red-1'"
              :style="{ flexWrap: $q.platform.is.mobile ? 'wrap !important' : 'no-wrap' }"
            >
              <q-item-section avatar>
                <q-img :src="getVariationImage(product.variations)" class="basket-list--product-image" width="75px" />
              </q-item-section>
              <template v-if="$q.platform.is.desktop">
                <q-item-section>
                  <q-item-label class="basket-list--product-title">
                    <a :href="`/${product.link}`">{{ product.name }}</a>
                  </q-item-label>
                  <q-item-label class="basket-list--product-attr-details">{{ getVariationDetail(product.attributes) }} </q-item-label>
                  <q-item-label v-if="hasBasketIssues" class="basket-list--product-warn-details">
                    <q-chip class="text-weight-bold q-ml-none" color="red-2" square v-if="!product.onsale"> Ürün artık satılmıyor </q-chip>
                    <q-chip class="text-weight-bold q-ml-none" color="red-2" square v-else-if="!product.instock"> Ürün stokta yok </q-chip>
                    <q-chip class="text-weight-bold q-ml-none" color="red-2" square v-else-if="!product.inquantity">
                      Maksimum stok adedi:
                      {{ product.variations[0].stock }}
                    </q-chip>
                  </q-item-label>
                </q-item-section>
                <q-item-section side class="basket-list--product-price text-black text-weight-bold">
                  {{ $numberToCurrency(product.variations[0].priceNumber * Number(productQuantity[product.variations[0].code])) || 0 }}
                  ₺
                </q-item-section>
              </template>
              <q-item-section side>
                <input-piece
                  v-model="productQuantity[product.variations[0].code]"
                  :disabled="basketQuantityLoading || !product.instock"
                  @increase="increaseQuantity(product.code, product.variations[0].code, Number(productQuantity[product.variations[0].code]))"
                  @decrease="decreaseQuantity(product.code, product.variations[0].code, Number(productQuantity[product.variations[0].code]))"
                  style="width: 120px"
                />
              </q-item-section>
              <q-item-section side class="text-black text-weight-bold">
                <div class="flex">
                  <q-btn
                    color="accent"
                    unelevated
                    icon="fa-solid fa-arrow-up-right-from-square"
                    class="text-black q-pa-sm q-mr-sm"
                    round
                    size=".55rem"
                    :to="product.link"
                  >
                    <q-tooltip class="text-body2 text-weight-bold bg-primary">Ürüne Git</q-tooltip>
                  </q-btn>
                  <q-btn
                    color="accent"
                    unelevated
                    text-color="red-5"
                    icon="fa-solid fa-trash"
                    class="text-black q-pa-sm"
                    round
                    size=".55rem"
                    @click="removeProductFromBasket(product.code, product.variations[0].code, Number(productQuantity[product.variations[0].code]))"
                  >
                    <q-tooltip class="text-body2 text-weight-bold bg-red-5">Sepetten Kaldır</q-tooltip>
                  </q-btn>
                </div>
              </q-item-section>

              <!--       Mobilde block'u alta atması için q-item-section tekrarı       -->
              <template v-if="$q.platform.is.mobile">
                <q-item-section :style="{ flexBasis: '100%' }">
                  <q-item-label class="basket-list--product-title" :style="{ marginTop: '1rem' }">
                    <a :href="`/${product.link}`">{{ product.name }}</a>
                  </q-item-label>
                  <q-item-label class="basket-list--product-attr-details">{{ getVariationDetail(product.attributes) }} </q-item-label>
                  <q-item-label v-if="hasBasketIssues" class="basket-list--product-warn-details">
                    <q-chip class="text-weight-bold q-ml-none" color="red-2" square v-if="!product.onsale"> Ürün artık satılmıyor </q-chip>
                    <q-chip class="text-weight-bold q-ml-none" color="red-2" square v-else-if="!product.instock"> Ürün stokta yok </q-chip>
                    <q-chip class="text-weight-bold q-ml-none" color="red-2" square v-else-if="!product.inquantity">
                      Maksimum stok adedi:
                      {{ product.variations[0].stock }}
                    </q-chip>
                  </q-item-label>
                </q-item-section>
                <q-item-section side class="basket-list--product-price text-black text-weight-bold" :style="{ paddingLeft: '0' }">
                  {{ $numberToCurrency(product.variations[0].priceNumber * Number(productQuantity[product.variations[0].code])) || 0 }}
                  ₺
                </q-item-section>
              </template>
              <!--       ./Mobilde block'u alta atması için q-item-section tekrarı       -->
            </q-item>
            <q-item v-if="basketListLoading">
              <div style="width: 100%" class="text-center">
                <q-linear-progress color="primary" indeterminate class="q-mb-lg" />
                <span class="text-h6 text-weight-bold">Sepet Yükleniyor</span>
              </div>
            </q-item>
            <q-item v-else-if="!basketList.length" class="no-product-any justify-center">
              <div class="text-center flex">
                <p>Sepetinizde ürün bulunmuyor</p>
              </div>
            </q-item>
          </q-list>
        </div>
        <div class="user-basket col-xs-12 col-md-4 q-pa-md">
          <q-list>
            <q-item class="q-px-none">
              <q-item-section>
                <q-item-label class="basket-list--head-title text-subtitle1 flex items-center">
                  <q-icon name="shopping_bag" size="1.3em" class="q-mr-sm" />
                  Sipariş Özeti
                </q-item-label>
                <q-item-label>
                  <q-separator />
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
          <q-card>
            <q-list>
              <q-item>
                <q-item-section>Toplam {{ basketList.length }} ürün</q-item-section>
              </q-item>
              <q-separator inset />
              <q-item>
                <q-item-section class="text-grey-9">Ürün Toplamı:</q-item-section>
                <q-item-section side class="text-grey-9">{{ $numberToCurrency(getProductsTotal()) }} ₺</q-item-section>
              </q-item>
              <q-item>
                <q-item-section class="text-grey-9">Kargo Bedeli:</q-item-section>
                <q-item-section side class="text-grey-9">{{ $numberToCurrency(Number(shippingCost)) }} ₺ </q-item-section>
              </q-item>
              <q-item>
                <q-item-section class="text-weight-bold">Toplam</q-item-section>
                <q-item-section side class="text-black text-weight-bold">
                  {{ basketList.length ? $numberToCurrency(getProductsTotal() + Number(shippingCost)) : 0 }} ₺
                </q-item-section>
              </q-item>
              <q-item>
                <q-item-section>
                  <q-btn
                    :disable="basketListLoading || basketQuantityLoading || !basketList.length"
                    glossy
                    color="green-14"
                    class="basket-list--complete-order-action text-capitalize text-subtitle1 flex items-center"
                    @click="checkInUserBasket"
                    :loading="checkInBasketLoading"
                  >
                    <q-icon v-show="hasBasketIssues" name="refresh" class="q-mr-sm" size="1.2rem" style="position: relative; top: 1px" />
                    {{ !hasBasketIssues ? 'Alışverişi Tamamla' : 'Sepeti Yenile' }}
                  </q-btn>
                </q-item-section>
              </q-item>
            </q-list>
          </q-card>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
#userBasketList .user-basket {
  font-family: 'EB Garamond', serif;
  font-size: 1rem;
}

.basket-list--product-image {
  border: 1px solid #bebebe;
  border-radius: 2px;
}

.basket-list--product-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
}

.basket-list--product-title a {
  color: var(--q-primary);
  text-decoration: none;
}

.basket-list--product-attr-details {
  font-weight: 100;
  color: #555;
  letter-spacing: 0.03rem;
}

.basket-list--product-warn-details {
  color: #555;
  letter-spacing: 0.03rem;
}

.basket-list--product-price {
  font-family: system-ui, -apple-system, Helvetica, sans-serif;
  font-size: 1.15rem;
  font-weight: 600;
}

.basket-list--head-title {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.no-product-any {
  padding: 1.2rem;
  font-size: 1.2rem;
  font-weight: 600;
  background: #ededed;
}

.no-product-any p {
  margin-bottom: 0;
  color: #444;
  letter-spacing: 0.04rem;
}

.basket-list--complete-order-action {
  font-family: system-ui, -apple-system, Helvetica, sans-serif;
  letter-spacing: 0.04rem;
  font-weight: 600;
}

.basket-list--no-stock-list {
  padding-top: 0;
  border-radius: 5px;
  padding-bottom: 0;
}

.basket-list--no-stock-warn {
  font-family: system-ui, -apple-system, Helvetica, sans-serif;
  margin-bottom: 0;
  letter-spacing: 0.035rem;
  font-size: 1rem;
}
</style>
